import goldenGateRequestTransformer from '@plangrid-private/web-api-clients/dist/rawClients/goldenGateRequestTransformer'
import RawApiClient, {
	addJsonContentTypeRequestTransformer,
	includeCredentialsRequestTransformer,
	addXRequestedByHeaderTransformer,
	throwNon200ResponseTransformer,
	parseBodyAsJsonResponseTransformer,
} from '@plangrid-private/web-api-clients/dist/rawClients/RawApiClient'
import { BugsnagNotify } from '@adsk/napi-bugsnag'

export const rawNapiClient = new RawApiClient({
	requestTransformers: [
		goldenGateRequestTransformer('network-api'),
		addJsonContentTypeRequestTransformer,
		includeCredentialsRequestTransformer,
		addXRequestedByHeaderTransformer,
	],
	responseTransformers: [
		throwNon200ResponseTransformer,
		parseBodyAsJsonResponseTransformer,
	],
})

export const rawNapiPhotoServiceClient = new RawApiClient({
	requestTransformers: [goldenGateRequestTransformer('network-api')],
	responseTransformers: [
		throwNon200ResponseTransformer,
		parseBodyAsJsonResponseTransformer,
	],
})

export const rawApiClient = new RawApiClient({
	responseTransformers: [
		throwNon200ResponseTransformer,
		parseBodyAsJsonResponseTransformer,
	],
})

const MAX_RETRIES = 5
const RETRY_DELAY_MS = 3000
const CACHE_DURATION_MS = 5000
const REQUEST_TIMEOUT_MS = 10000
const requestCache = new Map<string, Promise<any>>()
const dataCache = new Map<string, any>()

const retryRequest = async <T>(
	request: () => Promise<T>,
	retries = MAX_RETRIES
): Promise<T> => {
	try {
		return await Promise.race([
			request(),
			new Promise<T>((_resolve, reject) =>
				setTimeout(() => {
					reject(new Error('Request timed out'))
				}, REQUEST_TIMEOUT_MS)
			),
		])
	} catch (error: any) {
		const isRetryLeft = retries > 0
		const isNetworkError =
			['Request timed out', 'Failed to fetch', 'Load failed'].includes(
				error.message
			) ||
			/NetworkError/gi.test(error.message) ||
			/not valid JSON/gi.test(error.message) ||
			error.response?.status === 0

		if (isNetworkError && !isRetryLeft) {
			BugsnagNotify(error, {
				message: `Tried ${MAX_RETRIES} times to fetch data from the network, but failed.`,
			})
		} else if (!isNetworkError) {
			BugsnagNotify(error)
		}

		if (isRetryLeft && isNetworkError) {
			await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
			return await retryRequest(request, retries - 1)
		}
		throw error
	}
}

interface IGetOptions {
	cacheData?: boolean
}

const handleGETRequest = async (
	url: string,
	options?: IGetOptions
): Promise<any> => {
	if (!requestCache.has(url)) {
		if (options?.cacheData === true && dataCache.has(url)) {
			const cached = dataCache.get(url)
			if (Date.now() - cached.timestamp < CACHE_DURATION_MS) {
				return cached.data
			} else {
				dataCache.delete(url)
			}
		}
		const request = retryRequest(async () => {
			try {
				const data = await rawNapiClient.get(url)
				if (options?.cacheData === true) {
					dataCache.set(url, { data, timestamp: Date.now() })
				}
				return data
			} finally {
				requestCache.delete(url)
			}
		})
		requestCache.set(url, request)
	}
	return await requestCache.get(url)
}

const handleRequest = async (
	method: 'post' | 'put',
	url: string,
	body?: any
): Promise<any> => {
	if (!requestCache.has(url)) {
		const request = retryRequest(async () => {
			try {
				return await rawNapiClient[method](url, body)
			} finally {
				requestCache.delete(url)
			}
		})
		requestCache.set(url, request)
	}
	return await requestCache.get(url)
}

export const rawNapiClientWithRetry = {
	get: async (url: string, options?: IGetOptions) =>
		await handleGETRequest(url, options),
	post: async (url: string, body: any) =>
		await handleRequest('post', url, body),
	put: async (url: string, body: any) => await handleRequest('put', url, body),
}
